import React, { useState, useEffect } from 'react';
import './PropertyFiltersModal.css';

export const DEFAULT_FILTERS = {
  status: 'Active', // 'For Sale' — selected by default on page load
  homeTypes: [],      // 'single family' | 'townhouse' | 'condo' | 'multi-family'
  minPrice: '',
  maxPrice: '',
  beds: 'any',        // 'any' | 'studio' | '1'-'4' | '5+'
  baths: 'any',       // 'any' | '1' | '1.5' | '2' | '2.5' | '3' | '4'
  minSqft: '',
  maxSqft: '',
  minYearBuilt: '',
  minLotAcres: '',
  maxAssocFee: '',
  maxDaysOnMarket: '',
  garage: 'any',      // 'any' | '1' | '2' | '3' | '4'
  hasPool: false,
  hasSpa: false,
  hasView: false,
  hasWaterfront: false,
  hasFireplace: false,
  hasBasement: false,
  isSeniorCommunity: false,
  isNewConstruction: false,
  isFurnished: false,
  hasOpenHouse: false,
  hasVirtualTour: false,
};

// Maps filter state → MLS search API query params (mirrors mobile filtersToSearchParams)
export const filtersToSearchParams = (filters) => {
  const p = {};
  if (!filters) return p;

  if (filters.status) p.status = filters.status;

  if (filters.homeTypes && filters.homeTypes.length > 0) {
    // Send first selected type; API receives additional type params if array is supported
    p.type = filters.homeTypes[0];
  }

  if (filters.minPrice) p.minPrice = parseInt(filters.minPrice, 10);
  if (filters.maxPrice) p.maxPrice = parseInt(filters.maxPrice, 10);

  if (filters.beds && filters.beds !== 'any') {
    p.minBeds = filters.beds === 'studio' ? 0 : parseInt(filters.beds, 10);
  }
  if (filters.baths && filters.baths !== 'any') {
    p.minBaths = parseFloat(filters.baths);
  }

  if (filters.minSqft) p.minArea = parseInt(filters.minSqft, 10);
  if (filters.maxSqft) p.maxArea = parseInt(filters.maxSqft, 10);
  if (filters.minYearBuilt) p.minYearBuilt = parseInt(filters.minYearBuilt, 10);
  if (filters.minLotAcres) p.minLotAcres = parseFloat(filters.minLotAcres);
  if (filters.maxAssocFee) p.maxAssocFee = parseInt(filters.maxAssocFee, 10);
  if (filters.maxDaysOnMarket) p.maxDaysOnMarket = parseInt(filters.maxDaysOnMarket, 10);

  if (filters.garage && filters.garage !== 'any') {
    p.minGarageSpaces = parseInt(filters.garage, 10);
  }

  if (filters.hasPool) p.hasPool = true;
  if (filters.hasSpa) p.hasSpa = true;
  if (filters.hasView) p.hasView = true;
  if (filters.hasWaterfront) p.hasWaterfront = true;
  if (filters.hasFireplace) p.hasFireplace = true;
  if (filters.hasBasement) p.hasBasement = true;
  if (filters.isSeniorCommunity) p.isSeniorCommunity = true;
  if (filters.isNewConstruction) p.isNewConstruction = true;
  if (filters.isFurnished) p.isFurnished = true;
  if (filters.hasOpenHouse) p.hasOpenHouse = true;
  if (filters.hasVirtualTour) p.hasVirtualTour = true;

  return p;
};

export const countActiveFilters = (filters) => {
  if (!filters) return 0;
  let n = 0;
  if (filters.status && filters.status !== DEFAULT_FILTERS.status) n++;
  if (filters.homeTypes?.length > 0) n++;
  if (filters.minPrice || filters.maxPrice) n++;
  if (filters.beds !== 'any') n++;
  if (filters.baths !== 'any') n++;
  if (filters.minSqft || filters.maxSqft) n++;
  if (filters.minYearBuilt) n++;
  if (filters.minLotAcres) n++;
  if (filters.maxAssocFee) n++;
  if (filters.maxDaysOnMarket) n++;
  if (filters.garage !== 'any') n++;
  const features = [
    filters.hasPool, filters.hasSpa, filters.hasView, filters.hasWaterfront,
    filters.hasFireplace, filters.hasBasement, filters.isSeniorCommunity,
    filters.isNewConstruction, filters.isFurnished, filters.hasOpenHouse,
    filters.hasVirtualTour,
  ].filter(Boolean).length;
  if (features > 0) n++;
  return n;
};

// ─── sub-components ─────────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { label: 'Any',      value: '' },
  { label: 'For Sale', value: 'Active' },
  { label: 'Pending',  value: 'Pending' },
  { label: 'Sold',     value: 'Closed' },
  { label: 'For Rent', value: 'Rent' },
];

const HOME_TYPES = [
  {
    id: 'single family', label: 'House',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
      </svg>
    ),
  },
  {
    id: 'townhouse', label: 'Townhouse',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 22V9l5-5 5 5v13"/><path d="M13 22V9l5-5 5 5v13"/><line x1="1" y1="22" x2="23" y2="22"/>
      </svg>
    ),
  },
  {
    id: 'condo', label: 'Condo',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="1"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/><line x1="9" y1="9" x2="9" y2="21"/><line x1="15" y1="9" x2="15" y2="21"/>
      </svg>
    ),
  },
  {
    id: 'multi-family', label: 'Multi-family',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="7" width="20" height="15" rx="1"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/><line x1="12" y1="12" x2="12" y2="12"/><line x1="8" y1="12" x2="8" y2="12"/><line x1="16" y1="12" x2="16" y2="12"/><line x1="8" y1="16" x2="8" y2="16"/><line x1="16" y1="16" x2="16" y2="16"/><line x1="12" y1="16" x2="12" y2="16"/>
      </svg>
    ),
  },
];

const BEDS_OPTIONS = ['any', 'Studio', '1', '2', '3', '4', '5+'];
const BATHS_OPTIONS = [
  { label: 'Any', value: 'any' },
  { label: '1+',  value: '1' },
  { label: '1.5+', value: '1.5' },
  { label: '2+',  value: '2' },
  { label: '2.5+', value: '2.5' },
  { label: '3+',  value: '3' },
  { label: '4+',  value: '4' },
];
const GARAGE_OPTIONS = ['any', '1+', '2+', '3+', '4+'];

const FEATURES = [
  { key: 'hasPool',           label: 'Pool' },
  { key: 'hasSpa',            label: 'Spa' },
  { key: 'hasView',           label: 'View' },
  { key: 'hasWaterfront',     label: 'Waterfront' },
  { key: 'hasFireplace',      label: 'Fireplace' },
  { key: 'hasBasement',       label: 'Basement' },
  { key: 'isSeniorCommunity', label: 'Senior community (55+)' },
  { key: 'isNewConstruction', label: 'New construction' },
  { key: 'isFurnished',       label: 'Furnished' },
  { key: 'hasOpenHouse',      label: 'Open house' },
  { key: 'hasVirtualTour',    label: 'Virtual tour' },
];

// ─── main component ──────────────────────────────────────────────────────────

const PropertyFiltersModal = ({ isOpen, onClose, initialFilters, onApply }) => {
  const [draft, setDraft] = useState(DEFAULT_FILTERS);

  // Sync draft whenever the modal opens
  useEffect(() => {
    if (isOpen) setDraft(initialFilters || DEFAULT_FILTERS);
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lock body scroll while open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!isOpen) return null;

  const set = (key, value) => setDraft(prev => ({ ...prev, [key]: value }));

  const toggleHomeType = (id) => {
    setDraft(prev => {
      const cur = prev.homeTypes || [];
      return {
        ...prev,
        homeTypes: cur.includes(id) ? cur.filter(t => t !== id) : [...cur, id],
      };
    });
  };

  const toggleFeature = (key) => set(key, !draft[key]);

  const handleApply = () => { onApply(draft); onClose(); };
  const handleReset = () => setDraft(DEFAULT_FILTERS);

  const numInput = (key, placeholder, opts = {}) => (
    <input
      type="number"
      className="pfm-input"
      placeholder={placeholder}
      value={draft[key]}
      min={opts.min}
      step={opts.step}
      onChange={e => set(key, e.target.value)}
    />
  );

  return (
    <div className="pfm-overlay" onClick={onClose}>
      <div className="pfm-modal" onClick={e => e.stopPropagation()}>

        {/* ── Header ── */}
        <div className="pfm-header">
          <button className="pfm-close-btn" onClick={onClose} aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
          <span className="pfm-title">Filters</span>
          <button className="pfm-reset-link" onClick={handleReset}>Reset all</button>
        </div>

        {/* ── Scrollable body ── */}
        <div className="pfm-body">

          {/* Status */}
          <div className="pfm-section">
            <div className="pfm-section-title">Status</div>
            <div className="pfm-pill-row">
              {STATUS_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  className={`pfm-pill${draft.status === opt.value ? ' pfm-pill-active' : ''}`}
                  onClick={() => set('status', opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="pfm-divider" />

          {/* Home type */}
          <div className="pfm-section">
            <div className="pfm-section-title">Home type</div>
            <div className="pfm-home-type-grid">
              {HOME_TYPES.map(ht => {
                const active = draft.homeTypes?.includes(ht.id);
                return (
                  <button
                    key={ht.id}
                    className={`pfm-home-type-tile${active ? ' pfm-home-type-tile-active' : ''}`}
                    onClick={() => toggleHomeType(ht.id)}
                  >
                    <span className="pfm-home-type-icon">{ht.icon}</span>
                    <span className="pfm-home-type-label">{ht.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="pfm-divider" />

          {/* Price */}
          <div className="pfm-section">
            <div className="pfm-section-title">Price</div>
            <div className="pfm-range-row">
              <div className="pfm-price-input-wrap">
                <span className="pfm-currency">$</span>
                <input
                  type="number"
                  className="pfm-input pfm-input-price"
                  placeholder="No min"
                  value={draft.minPrice}
                  min={0}
                  onChange={e => set('minPrice', e.target.value)}
                />
              </div>
              <span className="pfm-range-dash">–</span>
              <div className="pfm-price-input-wrap">
                <span className="pfm-currency">$</span>
                <input
                  type="number"
                  className="pfm-input pfm-input-price"
                  placeholder="No max"
                  value={draft.maxPrice}
                  min={0}
                  onChange={e => set('maxPrice', e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="pfm-divider" />

          {/* Beds */}
          <div className="pfm-section">
            <div className="pfm-section-title">Beds</div>
            <div className="pfm-pill-row">
              {BEDS_OPTIONS.map(opt => {
                const val = opt.toLowerCase() === 'any' ? 'any' : opt.toLowerCase();
                return (
                  <button
                    key={opt}
                    className={`pfm-pill${draft.beds === val ? ' pfm-pill-active' : ''}`}
                    onClick={() => set('beds', val)}
                  >
                    {val === 'any' ? 'Any' : opt}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Baths */}
          <div className="pfm-section" style={{ paddingTop: 0 }}>
            <div className="pfm-section-title">Baths</div>
            <div className="pfm-pill-row">
              {BATHS_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  className={`pfm-pill${draft.baths === opt.value ? ' pfm-pill-active' : ''}`}
                  onClick={() => set('baths', opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="pfm-divider" />

          {/* Square footage */}
          <div className="pfm-section">
            <div className="pfm-section-title">Square footage</div>
            <div className="pfm-range-row">
              {numInput('minSqft', 'Min sqft', { min: 0 })}
              <span className="pfm-range-dash">–</span>
              {numInput('maxSqft', 'Max sqft', { min: 0 })}
            </div>
          </div>

          <div className="pfm-divider" />

          {/* Property details */}
          <div className="pfm-section">
            <div className="pfm-section-title">Property details</div>
            <div className="pfm-detail-grid">
              <div className="pfm-detail-item">
                <label className="pfm-detail-label">Year built (min)</label>
                {numInput('minYearBuilt', 'e.g. 2000', { min: 1800 })}
              </div>
              <div className="pfm-detail-item">
                <label className="pfm-detail-label">Lot size (min acres)</label>
                {numInput('minLotAcres', 'e.g. 0.25', { min: 0, step: 0.01 })}
              </div>
              <div className="pfm-detail-item">
                <label className="pfm-detail-label">HOA fee (max $/mo)</label>
                {numInput('maxAssocFee', 'e.g. 300', { min: 0 })}
              </div>
              <div className="pfm-detail-item">
                <label className="pfm-detail-label">Days on market (max)</label>
                {numInput('maxDaysOnMarket', 'e.g. 30', { min: 0 })}
              </div>
            </div>
          </div>

          <div className="pfm-divider" />

          {/* Garage */}
          <div className="pfm-section">
            <div className="pfm-section-title">Garage spaces</div>
            <div className="pfm-pill-row">
              {GARAGE_OPTIONS.map((opt, i) => {
                const val = i === 0 ? 'any' : String(i);
                return (
                  <button
                    key={opt}
                    className={`pfm-pill${draft.garage === val ? ' pfm-pill-active' : ''}`}
                    onClick={() => set('garage', val)}
                  >
                    {val === 'any' ? 'Any' : opt}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="pfm-divider" />

          {/* Features */}
          <div className="pfm-section">
            <div className="pfm-section-title">Features</div>
            <div className="pfm-features-grid">
              {FEATURES.map(f => (
                <label key={f.key} className="pfm-feature-item">
                  <input
                    type="checkbox"
                    className="pfm-checkbox"
                    checked={!!draft[f.key]}
                    onChange={() => toggleFeature(f.key)}
                  />
                  <span className="pfm-feature-label">{f.label}</span>
                </label>
              ))}
            </div>
          </div>

        </div>

        {/* ── Footer ── */}
        <div className="pfm-footer">
          <button className="pfm-footer-reset" onClick={handleReset}>Reset all</button>
          <button className="pfm-apply-btn" onClick={handleApply}>Apply filters</button>
        </div>

      </div>
    </div>
  );
};

export default PropertyFiltersModal;
